import { useState, useContext, useCallback, useEffect } from 'react';
import { Banner, FontAwesomeIcon, Stack } from '@rivallapp/volosports-components';
import { useMutation } from '@apollo/client';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useHistory } from 'react-router-dom';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';
import classNames from 'classnames/bind';

import { siteUrl } from '../../../../../environment';
import styles from './PaymentDaily.module.scss';
import { Popup, Button, LoginModal } from '../../../../../components';
import { SubmitButton } from '../../SharedComponents';
import {
  useActionStates,
  useCurrentUser,
  useRegistrationData,
  useVoloPassDetails,
  useIntlDateTimeFormat,
} from '../../../../../hooks';
import { RegistrationActions } from '../../../../../contexts/RegistrationContext/RegistrationReducer';

import { GameProps } from '../../../../../types';
import { RegistrationContext } from '../../../../../contexts';
import { registerAndPay } from '../../../Register/tools/helpers';
import { getDailyPricingData } from '../../../../../hooks/useDropinLeagueData/helpers';
import DailyPaymentSummary from '../DailyPaymentSummary/DailyPaymentSummary';
import { REGISTER_FOR_PROGRAM } from '../../../../../hooks/useRegistrationData/QUERIES_AND_MUTATIONS';

const { UPDATE_REGISTRATION_STATE } = RegistrationActions;

const cx = classNames.bind(styles);

const PaymentDaily = props => {
  const { game } = props;

  const {
    updating: dailyUpdating,
    setUpdating,
    setSuccess,
    setError,
  } = useActionStates({ withAlerts: true });

  const history = useHistory();

  const [didCopy, setDidCopy] = useState(false);
  const [editPayment, setEditPayment] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const [registerMutation] = useMutation(REGISTER_FOR_PROGRAM);
  const [registerState, registerDispatch] = useContext(RegistrationContext);

  const { currentUser = undefined } = useCurrentUser();
  const { _id: currentUserId } = currentUser || {};

  const { selectedCity, startMembershipMutation, updateUserPlanMutation, startTrialMembership } =
    useVoloPassDetails();

  const {
    isMember,
    isRsvpdForDropIn,
    isEnrolledInProgram,
    registerUpdating,
    isEnrolledRefetch,
    confirmDropInRsvp,
    registrationClosed,
    registerDailyLoading,
  } = useRegistrationData();

  const {
    league,
    loading,
    addVpTrial,
    waiverAgreed,
    cancellationPolicyAgreed,
    paymentSources,
    donationAmount,
    addVpMembership,
    dropInPriceData,
    showConfirmation,
    pricingBreakdown,
    processingPayment,
    creditAppliedAmount,
    usePaymentSummaryProps,
  } = registerState;

  const {
    memberPrice: dropInMemberPrice,
    nonMemberPrice,
    memberFee,
    nonMemberFee,
    totalMemberPrice,
    totalNonMemberPrice,
  } = getDailyPricingData(dropInPriceData) || {};

  const { totalCents } = pricingBreakdown || {};
  const vpInCart = addVpTrial || addVpMembership;
  const shareUrl = game?._id
    ? `${siteUrl}/game/${game?._id}`
    : () => setError(`Could not copy link. No program found: ${game?._id}`);

  const [dropInPrice, setDropInPrice] = useState(0);
  const [dropInFee, setDropInFees] = useState(0);
  const [dropInTotal, setDropInTotal] = useState(0);

  const earlyBird = league?.registration?.phases?.earlyBird;
  const earlyBirdHasPassed = new Date() > new Date(earlyBird?.endDate);
  const isDropIn = !!game;

  // Formats as "8/18"
  const formatter = useIntlDateTimeFormat(undefined, {
    day: 'numeric',
    month: 'numeric',
    timeZone: league?.venue?.timezone,
  });

  useEffect(() => {
    if (isMember && dropInMemberPrice !== null && dropInMemberPrice !== undefined) {
      setDropInPrice(dropInMemberPrice);
      setDropInFees(memberFee);
      setDropInTotal(totalMemberPrice);
    } else if (!isMember) {
      setDropInPrice(nonMemberPrice);
      setDropInFees(nonMemberFee);
      setDropInTotal(totalNonMemberPrice);
    }
  }, [
    isMember,
    dropInMemberPrice,
    memberFee,
    totalMemberPrice,
    nonMemberPrice,
    nonMemberFee,
    totalNonMemberPrice,
  ]);

  const totalDropInCents = (dropInTotal || 0) + (donationAmount || 0) - (creditAppliedAmount || 0);

  const updating = registerUpdating || dailyUpdating || processingPayment;

  const onSubmitRegistration = useCallback(async () => {
    try {
      await registerAndPay(registerMutation, {
        ...registerState,
        game,
        history,
        setError,
        setSuccess,
        setUpdating,
        selectedCity,
        registerDispatch,
        startTrialMembership,
        updateUserPlanMutation,
        startMembershipMutation,
      });

      await isEnrolledRefetch();
    } catch (e) {
      setError(e);
    }
  }, [
    game,
    history,
    setError,
    setSuccess,
    setUpdating,
    selectedCity,
    registerState,
    registerDispatch,
    registerMutation,
    isEnrolledRefetch,
    startTrialMembership,
    updateUserPlanMutation,
    startMembershipMutation,
  ]);

  const onRegistrationClick = () => {
    if (!currentUser) setShowLogin(true);
    else if (currentUserId && isEnrolledInProgram && !game?._id) {
      history.push(`/app/member/${league?._id}`);
    } else if (currentUserId && isRsvpdForDropIn && game?._id) {
      history.push(`/app/${game?._id}`);
    } else if (currentUserId && !isRsvpdForDropIn && game?._id) {
      confirmDropInRsvp(game);
    } else if (
      currentUserId &&
      !isRsvpdForDropIn &&
      !game?._id &&
      !isEnrolledInProgram &&
      league?._id
    ) {
      onSubmitRegistration();
    } else {
      setError("Didn't expect to make it to this case!");
    }
  };

  const buttonLabel = (() => {
    if (!currentUserId) return 'Log In';
    if ((isEnrolledInProgram && !game) || (game && isRsvpdForDropIn)) {
      return 'View Registration';
    }
    if (!isRsvpdForDropIn && registrationClosed) {
      return 'Registration Closed';
    }
    if (!isMember && league?.is_volo_pass_exclusive) {
      return 'Become a Member and Register';
    }
    if (!game?._id && totalCents && totalCents > 0) {
      return 'Pay and Register';
    }
    if (game?._id && totalDropInCents && totalDropInCents > 0) {
      return 'Pay and Register';
    }
    if (!game?._id && (!totalCents || totalCents === 0)) {
      return 'Register';
    }
    if (game?._id && (!totalDropInCents || totalDropInCents === 0)) {
      return 'Register';
    }
    return 'Register';
  })();

  const buttonDisabled = (() => {
    // Not logged in - Button is clickable to allow login
    if (!currentUserId) return false;

    // Page still loading
    if (updating || loading || registerDailyLoading) return true;

    // User hasn't agreed to applicable waivers
    if (!waiverAgreed) return true;
    if (!cancellationPolicyAgreed) return true;

    // User hasn't agreed to membership terms and conditions
    if ((addVpMembership || addVpTrial) && !usePaymentSummaryProps.areMemberTermsAgreed)
      return true;

    const isDropIn = Boolean(game);

    /**
     * User needs to have a payment method if:
     *  - The program is not free, OR
     *  - They have Volo Pass in their cart (Trial membership excluded from this)
     */
    const programIsNotFree = (isDropIn ? totalDropInCents : totalCents) > 0;
    if (!paymentSources?.length && (programIsNotFree || addVpMembership)) return true;

    // If the game is a drop-in, they can't register again for the drop-in.
    if (isDropIn && isRsvpdForDropIn) return true;

    // If the program is any other daily, they can't register again.
    if (!isDropIn && isEnrolledInProgram) return true;

    // If the program is Member Exclusive and the user is not a member,
    // they will have to add Volo Pass to their cart before they can register.
    if (league?.is_volo_pass_exclusive && !isMember && !(addVpMembership || addVpTrial))
      return true;

    // The button is clickable!
    return false;
  })();

  return (
    <>
      <Popup
        hidden={!showConfirmation}
        close={() => {
          registerDispatch({
            type: UPDATE_REGISTRATION_STATE,
            update: { showConfirmation: false },
          });
          history.push('/app/dashboard');
        }}
        className={cx('popupContainer')}
      >
        <div className={cx('confirmContainer')}>
          <Stack marginY="2" justifyContent="center" flexDirection="row" alignItems="center">
            <FontAwesomeIcon
              justifyContent="center"
              icon={faCheckCircle}
              colorScheme="success"
              size="5xl"
            />
          </Stack>
          <h3 className="p-2">Your spot has been confirmed!</h3>
          <div className={cx('confirmText')}>
            You may cancel your spot up to 6 hours before the program start time or penalties apply.
            See the Cancellation and No-Show Policy for more details.
          </div>
          <div className={cx('share')}>
            <Button
              onClick={() => history.push('/app/dashboard')}
              className={cx('dashboardButton')}
            >
              Dashboard
            </Button>
            <CopyToClipboard
              text={shareUrl}
              onCopy={() => {
                setDidCopy(true);
                setSuccess('Invite link copied!');
              }}
            >
              <div data-for="c2c" data-tip={didCopy ? 'Invite link copied!' : 'Copy invite link'}>
                <Button className={cx('shareButton')}>Invite Friend</Button>
              </div>
            </CopyToClipboard>
          </div>
        </div>
      </Popup>
      <div className={cx('paymentBackground', 'col-md-6 col-lg-4 mt-2')}>
        {(isRsvpdForDropIn || (isEnrolledInProgram && !game)) && (
          <div className={cx('is-enrolled')}>
            You are already registered! Click below to see your registration details.
          </div>
        )}

        {/* Drop-ins should not have the early-bird banner. */}
        {!isDropIn && earlyBird?.allow && !earlyBirdHasPassed && (
          <Banner
            display="block"
            textAlign="center"
            borderRadius="none"
            fontSize="sm"
            fontWeight="600"
          >
            Early Bird Registration Ends {formatter.format(new Date(earlyBird?.endDate))}
          </Banner>
        )}

        <div className={cx('paymentBackgroundContent')}>
          {!loading &&
            !registerDailyLoading &&
            ((!game && !isEnrolledInProgram) || (game && !isRsvpdForDropIn)) && (
              <DailyPaymentSummary
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...{
                  ...props,
                  vpInCart,
                  editPayment,
                  setEditPayment,
                  dropInPrice,
                  dropInFee,
                  totalDropInCents,
                  currentUserId,
                }}
              />
            )}
          <SubmitButton
            buttonAction={onRegistrationClick}
            buttonDisabled={buttonDisabled}
            buttonLabel={buttonLabel}
            updatingLabel="Registering..."
            updating={updating}
            activityName={league.activityName}
            totalCents={vpInCart ? 0 : totalDropInCents}
            organizationName={league?.organization?.name}
            dropInId={game?._id || league?._id}
            currentUserId={currentUserId}
            addedVoloPass={vpInCart}
          />
        </div>
      </div>
      <LoginModal showLogin={showLogin} setShowLogin={setShowLogin} />
    </>
  );
};

PaymentDaily.propTypes = {
  game: GameProps,
};

PaymentDaily.defaultProps = {
  game: null,
};

export default PaymentDaily;
